// Single source of truth for which color each Home chart series gets. The hex
// values live as theme tokens (--chart-*) in global.scss, one set per theme.

// Reusable base colors. To add one, define --chart-<name> in global.scss (light
// and dark blocks) and reference it here by name.
export const CHART_COLORS = {
  skyBlue: 'var(--chart-sky-blue)',
  blue: 'var(--chart-blue)',
  midBlue: 'var(--chart-mid-blue)',
  navy: 'var(--chart-navy)',
  royalBlue: 'var(--chart-royal-blue)',
  deepBlue: 'var(--chart-deep-blue)',
  usBlue: 'var(--chart-us-blue)',
  cyan: 'var(--chart-cyan)',
  teal: 'var(--chart-teal)',
  aqua: 'var(--chart-aqua)',
  green: 'var(--chart-green)',
  darkTeal: 'var(--chart-dark-teal)',
  red: 'var(--chart-red)',
  coral: 'var(--chart-coral)',
  crimson: 'var(--chart-crimson)',
  brightRed: 'var(--chart-bright-red)',
  orange: 'var(--chart-orange)',
  amber: 'var(--chart-amber)',
  gold: 'var(--chart-gold)',
  sand: 'var(--chart-sand)',
  yellow: 'var(--chart-yellow)',
  sunYellow: 'var(--chart-sun-yellow)',
  upsYellow: 'var(--chart-ups-yellow)',
  dhlYellow: 'var(--chart-dhl-yellow)',
  purple: 'var(--chart-purple)',
  fedexPurple: 'var(--chart-fedex-purple)',
  brown: 'var(--chart-brown)',
  slate: 'var(--chart-slate)',
  gray: 'var(--chart-gray)',
  lightGray: 'var(--chart-light-gray)',
  silver: 'var(--chart-silver)',
  darkGray: 'var(--chart-dark-gray)',
  periwinkle: 'var(--chart-periwinkle)',
  mint: 'var(--chart-mint)',
  terracotta: 'var(--chart-terracotta)',
  plum: 'var(--chart-plum)',
  softPeriwinkle: 'var(--chart-soft-periwinkle)',
  charcoal: 'var(--chart-charcoal)',
  sage: 'var(--chart-sage)',
  forest: 'var(--chart-forest)',
  deepForest: 'var(--chart-deep-forest)',
} as const;

const C = CHART_COLORS;

export const DEFAULT_SERIES_COLOR = C.skyBlue;

// shipments (non-status tabs), alerts, transport provider exceptions, invoice disputes
const GENERAL_COLORS: Record<string, string> = {
  Delivered: C.skyBlue,
  'Delivery exception': C.red,
  'In transit': C.blue,
  'Out for delivery': C.purple,
  'Awaiting pick up': C.gray,
  Pending: C.amber,
  'Not found': C.red,
  Incomplete: C.orange,
  Rated: C.gray,
  Cancelled: C.darkGray,
  'On time': C.teal,
  Early: C.yellow,
  Late: C.coral,
  Other: C.gray,
  'No issue': C.lightGray,
  'At-risk alert': C.gold,
  'Critical alert': C.red,
  UPS: C.upsYellow,
  FedEx: C.orange,
  UPSP: C.deepBlue,
  Open: C.navy,
  Dismissed: C.slate,
  Resolved: C.green,
  Denied: C.red,
  Paid: C.green,
};

// shipments chart, "status" (Progress) tab
const SHIPMENT_STATUS_COLORS: Record<string, string> = {
  ...GENERAL_COLORS,
  Delivered: C.teal,
  'Not found': C.orange,
};

// otd, otp, schedule adherence
const ON_TIME_COLORS: Record<string, string> = {
  Early: C.blue,
  'On time': C.teal,
  Late: C.red,
  'All shipments': C.teal,
  'Excluding weather-delayed shipments': C.yellow,
};

const CYCLE_TIME_COLORS: Record<string, string> = {
  'Last mile delivery': C.aqua,
  'Failed delivery attempt': C.crimson,
  'In transit': C.midBlue,
  'Hub / Customs': C.sand,
  'Pre-pickup': C.silver,
};

// cost + freight markup; lookup is case-insensitive
export const CARRIER_COLORS: Record<string, string> = {
  fedex: C.fedexPurple,
  ups: C.brown,
  usps: C.usBlue,
  dhl: C.dhlYellow,
  'dhl ecommerce': C.dhlYellow,
  ontrac: C.brightRed,
  lasership: C.cyan,
  biocair: C.darkTeal,
  'bio cair': C.darkTeal,
  'world courier': C.royalBlue,
  'cencora world courier': C.royalBlue,
};

// colors handed to carriers the map above doesn't know, in hash order
export const CARRIER_FALLBACK_COLORS = [
  C.midBlue,
  C.aqua,
  C.sand,
  C.crimson,
  C.periwinkle,
  C.mint,
  C.terracotta,
  C.plum,
] as const;

// alert time to action: one color per phase
const TIME_TO_ACTION_COLORS: Record<string, string> = {
  'Time to acknowledge': C.navy,
  'Time to assign': C.slate,
  'Time to close': C.teal,
};

export const SERIES_PALETTES = {
  general: GENERAL_COLORS,
  shipmentStatus: SHIPMENT_STATUS_COLORS,
  onTime: ON_TIME_COLORS,
  cycleTime: CYCLE_TIME_COLORS,
  timeToAction: TIME_TO_ACTION_COLORS,
} as const;

export type SeriesPaletteKey = keyof typeof SERIES_PALETTES;

// Alert insights status bars, keyed by status
export const ALERT_STATUS_COLOR_BY_KEY: Record<
  'open' | 'resolved' | 'dismissed',
  string
> = {
  open: C.navy,
  resolved: C.green,
  dismissed: C.slate,
};

// Cycle time phase colors in the drawer's legend and per-shipment bar
export const CYCLE_PHASE_DRAWER_COLORS = {
  pickup_hours: 'var(--text-tertiary)',
  dwelling_hours: C.amber,
  moving_hours: 'var(--light-blue)',
  delivery_attempt_hours: 'var(--danger)',
  last_mile_hours: C.green,
} as const;

export const TRANSIT_TIME_COLOR = C.sunYellow;

// Solid fills keep grid lines behind the bars; legend hover dims other series.
export const CHART_BAR_OPACITY = 1;
